import * as RadixPopover from "@radix-ui/react-popover";
import { forwardRef, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { cn } from "@/lib/cn";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import {
  floatingCollisionPadding,
  floatingLayerClassName,
  searchableSelectContentCollisionClassName,
  searchableSelectListboxCollisionClassName
} from "@/components/ui/overlays/overlayPositioning";
import { Chip } from "@/components/primitives/Chip";

export type TagInputProps = {
  /** Current tags (controlled). */
  value: string[];
  /** Fired with the updated list on add / remove. */
  onChange: (value: string[]) => void;
  /** Optional existing-tag suggestions for autocomplete (filtered as you type). */
  suggestions?: string[];
  /** Allow committing values outside `suggestions` (default true). */
  creatable?: boolean;
  placeholder?: string;
  /** Max suggestions shown in the dropdown (default 8). */
  maxSuggestions?: number;
  size?: "sm" | "md";
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

export const TagInput = forwardRef<HTMLDivElement, TagInputProps>(
  (
    {
      value,
      onChange,
      suggestions = [],
      creatable = true,
      placeholder = "Add tag",
      maxSuggestions = 8,
      size = "md",
      disabled,
      error,
      className,
      id,
      "aria-describedby": describedBy,
      "aria-invalid": ariaInvalid
    },
    ref
  ) => {
    const [input, setInput] = useState("");
    const [focused, setFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const q = input.trim().toLowerCase();
    const matches = useMemo(
      () =>
        q
          ? suggestions
              .filter((s) => s.toLowerCase().includes(q) && !value.includes(s))
              .slice(0, maxSuggestions)
          : [],
      [q, suggestions, value, maxSuggestions]
    );

    const open = focused && matches.length > 0;

    const addTag = (raw: string) => {
      const tag = raw.trim();
      if (tag && !value.includes(tag)) onChange([...value, tag]);
      setInput("");
      setActiveIndex(0);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        if (open && matches[activeIndex]) {
          addTag(matches[activeIndex]);
        } else if (creatable) {
          addTag(input);
        }
      } else if (e.key === "Backspace" && !input && value.length) {
        onChange(value.slice(0, -1));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, matches.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Escape") {
        setFocused(false);
        inputRef.current?.blur();
      }
    };

    const hasTags = value.length > 0;

    return (
      <RadixPopover.Root open={open} onOpenChange={(next) => { if (!next) setFocused(false); }}>
        <RadixPopover.Anchor asChild>
          <div
            ref={ref}
            data-filled={hasTags && !error ? "" : undefined}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? error}
            className={cn(
              "bfrs-flex bfrs-w-full bfrs-cursor-text bfrs-flex-wrap bfrs-items-center bfrs-gap-1.5 bfrs-rounded-[var(--bfrs-input-radius)] bfrs-border bfrs-bg-surface-card bfrs-px-3 bfrs-transition bfrs-duration-150",
              "focus-within:bfrs-border-border-focus focus-within:bfrs-ring-2 focus-within:bfrs-ring-border-focus/20",
              size === "sm" ? "bfrs-min-h-7 bfrs-py-1" : "bfrs-min-h-9 bfrs-py-1.5",
              error
                ? "bfrs-border-danger focus-within:bfrs-border-danger focus-within:bfrs-ring-danger/15"
                : cn("bfrs-border-border", size === "sm" && "data-[filled]:bfrs-border-border-focus"),
              disabled && "bfrs-cursor-not-allowed bfrs-bg-surface-app bfrs-opacity-70",
              className
            )}
            onClick={() => !disabled && inputRef.current?.focus()}
          >
            {value.map((tag) => (
              <Chip
                key={tag}
                size={size === "sm" ? "xs" : "sm"}
                tone="primary"
                variant="soft"
                removable={!disabled}
                removeLabel={`Remove ${tag}`}
                onRemove={disabled ? undefined : () => onChange(value.filter((t) => t !== tag))}
              >
                {tag}
              </Chip>
            ))}
            <input
              ref={inputRef}
              id={id}
              value={input}
              disabled={disabled}
              placeholder={hasTags ? undefined : placeholder}
              aria-autocomplete="list"
              aria-expanded={open}
              onChange={(e) => {
                setInput(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              className="bfrs-min-w-[80px] bfrs-flex-1 bfrs-border-none bfrs-bg-transparent bfrs-text-[12px] bfrs-text-text-primary bfrs-outline-none placeholder:bfrs-text-text-placeholder disabled:bfrs-cursor-not-allowed disabled:bfrs-text-text-disabled"
            />
          </div>
        </RadixPopover.Anchor>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <RadixPopover.Content
              align="start"
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              onOpenAutoFocus={(e) => e.preventDefault()}
              className={cn(
                floatingLayerClassName,
                "bfrs-w-[var(--radix-popover-anchor-width)] bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-p-1 bfrs-shadow-elevated bfrs-outline-none bfrs-animate-content-in",
                searchableSelectContentCollisionClassName
              )}
            >
              <div role="listbox" className={searchableSelectListboxCollisionClassName}>
                {matches.map((s, index) => (
                  <button
                    key={s}
                    type="button"
                    role="option"
                    aria-selected={activeIndex === index}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      addTag(s);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      "bfrs-flex bfrs-w-full bfrs-cursor-pointer bfrs-items-center bfrs-rounded-sm bfrs-text-left bfrs-text-[12px] bfrs-text-text-primary bfrs-outline-none hover:bfrs-bg-primary-soft",
                      size === "md" ? "bfrs-h-9 bfrs-px-3" : "bfrs-h-7 bfrs-px-2.5",
                      activeIndex === index && "bfrs-bg-primary-soft"
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </RadixPopover.Content>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  }
);

TagInput.displayName = "TagInput";

export const TagsInput = TagInput;
export type TagsInputProps = TagInputProps;
